import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useActiveOrganization, useSession } from '@/lib/auth-client'
import { getOrgPlanStatus } from '@/lib/billing/server'
import { getSupportIdentity } from '@/lib/support/server'
import {
  capture,
  identify,
  register,
  resetAnalytics,
  setSupportIdentity,
} from './posthog'

/**
 * Bridges Better Auth session/org state into PostHog identity. Renders nothing —
 * mount it once inside `PostHogProvider` near the root (so it only runs when
 * analytics is enabled). Org context is attached as super properties rather than
 * a `group()` call: super properties ride on every event and can be filtered /
 * broken down for free, whereas Group Analytics is a paid add-on.
 */
export function AnalyticsIdentify() {
  const { data: session } = useSession()
  const { data: activeOrg } = useActiveOrganization()
  const orgId = activeOrg?.id
  const userId = session?.user?.id
  // Guards the once-per-signup capture below against effect re-runs.
  const signupCaptured = useRef(false)

  // Reuses the cache the dashboard/billing/credentials routes already populate;
  // only fires once an org is active, so marketing pages don't trigger it.
  const { data: plan } = useQuery({
    queryKey: ['org-plan', orgId],
    queryFn: () => getOrgPlanStatus(),
    enabled: !!orgId,
  })

  // The hash is a pure function of the user id, so fetch it once per user.
  const { data: supportIdentity } = useQuery({
    queryKey: ['support-identity', userId],
    queryFn: () => getSupportIdentity(),
    enabled: !!userId,
    staleTime: Number.POSITIVE_INFINITY,
  })

  useEffect(() => {
    const user = session?.user
    if (!user) {
      // `session` is `null` once Better Auth resolves a logged-out state (vs
      // `undefined` while still loading). Clear identity + super properties.
      if (session === null) resetAnalytics()
      return
    }
    identify(user.id, {
      email: user.email,
      name: user.name,
      email_verified: user.emailVerified,
      role: user.role ?? undefined,
      created_at: user.createdAt,
    })
    // First landing after a social signup: Better Auth sent this new user to
    // `newUserCallbackURL`, which carries `?signup=<provider>` (see
    // SocialSignIn). Email signups are captured on the form itself, so this only
    // fires for OAuth. Capture once, after identify() so it attributes to the
    // user, then strip the marker so a refresh can't re-fire it.
    //
    // `signup_submitted`, NOT `signup`: the authoritative conversion is emitted
    // server-side from the account-creation hook (see lib/server/auth.ts) and is
    // what the ad destinations read. Naming this one `signup` too would send a
    // SECOND SignUp conversion for every OAuth account — one carrying the click
    // id and conversion_id, one carrying neither, so the ad platform could not
    // deduplicate them.
    if (typeof window !== 'undefined' && !signupCaptured.current) {
      const params = new URLSearchParams(window.location.search)
      const method = params.get('signup')
      if (method) {
        signupCaptured.current = true
        capture('signup_submitted', { method })
        params.delete('signup')
        const query = params.toString()
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
        )
      }
    }
  }, [session])

  // Declared after the identify effect so it runs after it in the same commit:
  // setSupportIdentity must follow identify() (see its doc). The id check skips
  // a hash still cached for a user who has since signed out or switched.
  useEffect(() => {
    if (!supportIdentity || supportIdentity.distinctId !== userId) return
    setSupportIdentity(supportIdentity.distinctId, supportIdentity.hash)
  }, [supportIdentity, userId])

  // Org identity/slug immediately on switch; plan fields once the (cached) plan
  // query resolves. register() merges, so the two run independently.
  useEffect(() => {
    if (!activeOrg) return
    register({
      org_id: activeOrg.id,
      org_name: activeOrg.name,
      org_slug: activeOrg.slug,
    })
  }, [activeOrg])

  useEffect(() => {
    if (!orgId || !plan) return
    register({
      org_plan: plan.planSlug,
      org_plan_name: plan.planName,
      org_has_subscription: plan.hasSubscription,
      org_entitled: plan.entitled,
      org_included_gb: plan.includedGb,
    })
  }, [orgId, plan])

  return null
}
